import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, AlertTriangle, CheckCircle2, Clock, Package, Pencil, X, GitBranch, ArrowRight, ShoppingCart, Factory, ChevronDown, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

/**
 * MaterialLotHistory — full lot history for one raw material.
 *
 *   - Loads from GET /api/inventory/raw-materials/:id/lot-history
 *   - Toggle: All / Active only / Consumed only
 *   - Vendor filter dropdown (auto-populated from the material's history)
 *   - Each row shows: lot no, vendor, received date, finished date (or "in use"),
 *     orig qty / remaining qty / consumed %, cost / value
 *   - Admin gets a delete button (active lots only)
 *
 * Props:
 *   - materialId, materialName, unit, isAdmin, canSeePrices, onChanged()
 *   - requestDelete(url, name, message) — wired to the existing admin-delete hook
 */
export default function MaterialLotHistory({
  materialId, materialName, unit, isAdmin, canSeePrices,
  requestDelete, onChanged,
}) {
  const [data, setData] = useState({ lots: [], vendors: [], material_sales: [], total_sold: 0, active_count: 0, consumed_count: 0, total_count: 0 });
  const [loading, setLoading] = useState(true);
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all · active · consumed
  const [showSales, setShowSales] = useState(true);
  // Edit-lot modal state
  const [editLot, setEditLot] = useState(null); // null | lot object
  // iter164 — Trace Lot modal state
  const [traceLot, setTraceLot] = useState(null);   // {id, lot_no}
  const [traceData, setTraceData] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    lot_no: '', quantity_remaining: '', quantity_original: '', cost_per_unit: '',
    vendor_name: '', purchase_date: '', expiry_date: '', bill_ref: '', fifo_priority: false,
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (lot) => {
    setEditLot(lot);
    setEditForm({
      lot_no: lot.lot_no || '',
      quantity_remaining: lot.quantity_remaining ?? '',
      quantity_original: lot.quantity_original ?? '',
      cost_per_unit: lot.cost_per_unit ?? '',
      vendor_name: lot.vendor_name || '',
      purchase_date: (lot.purchase_date || '').slice(0, 10),
      expiry_date: (lot.expiry_date || '').slice(0, 10),
      bill_ref: lot.bill_ref || '',
      fifo_priority: !!lot.fifo_priority,
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault(); setSavingEdit(true);
    try {
      await axios.put(`${API}/inventory/lots/${editLot.id}`, editForm, { withCredentials: true });
      setEditLot(null);
      toast.success(`Lot ${editForm.lot_no} updated`);
      onChanged?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error saving lot');
    } finally { setSavingEdit(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = vendorFilter ? `?vendor=${encodeURIComponent(vendorFilter)}` : '';
      const res = await axios.get(`${API}/inventory/raw-materials/${materialId}/lot-history${params}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load lot history');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [materialId, vendorFilter]);

  const filteredLots = useMemo(() => {
    if (statusFilter === 'active')   return data.lots.filter(l => l.is_active);
    if (statusFilter === 'consumed') return data.lots.filter(l => !l.is_active);
    return data.lots;
  }, [data.lots, statusFilter]);

  return (
    <div className="bg-zinc-950/60 border-b border-zinc-800 p-3" data-testid={`lot-history-${materialId}`}>
      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">Lot History</span>
        <span className="text-[10px] text-zinc-600">·</span>
        {[
          { key: 'all',      label: `All ${data.total_count}`,       color: 'amber' },
          { key: 'active',   label: `Active ${data.active_count}`,   color: 'green' },
          { key: 'consumed', label: `Consumed ${data.consumed_count}`, color: 'zinc' },
        ].map(f => (
          <button key={f.key}
            data-testid={`lot-filter-${f.key}`}
            onClick={(e) => { e.stopPropagation(); setStatusFilter(f.key); }}
            className={`text-[10px] px-2 py-0.5 rounded-md border transition-colors ${
              statusFilter === f.key
                ? `bg-${f.color}-500/20 text-${f.color}-300 border-${f.color}-500/40 font-semibold`
                : 'bg-zinc-900 text-zinc-500 border-zinc-800 hover:border-zinc-700'
            }`}>
            {f.label}
          </button>
        ))}
        {data.vendors && data.vendors.length > 0 && (
          <>
            <span className="text-[10px] text-zinc-600 ml-2">Vendor:</span>
            <select
              value={vendorFilter}
              onChange={(e) => { e.stopPropagation(); setVendorFilter(e.target.value); }}
              onClick={(e) => e.stopPropagation()}
              data-testid="lot-vendor-filter"
              className="bg-zinc-900 border border-zinc-800 rounded-md px-1.5 py-0.5 text-zinc-300 text-[10px] focus:outline-none">
              <option value="">All vendors</option>
              {data.vendors.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </>
        )}
      </div>

      {loading ? (
        <div className="text-zinc-500 text-xs py-4 text-center">Loading lot history…</div>
      ) : filteredLots.length === 0 ? (
        <div className="text-zinc-500 text-xs py-4 text-center">
          {data.total_count === 0 ? `No lots yet for ${materialName}` : 'No lots match this filter'}
        </div>
      ) : (
        <div className="overflow-x-auto max-w-full">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="border-b border-zinc-800/60">
              <th className="text-left text-zinc-500 text-[10px] font-medium px-2 py-2 pl-8">Lot No</th>
              <th className="text-left text-zinc-500 text-[10px] font-medium px-2 py-2">Vendor</th>
              <th className="text-left text-zinc-500 text-[10px] font-medium px-2 py-2">Vendor Inv/Bill No</th>
              <th className="text-left text-zinc-500 text-[10px] font-medium px-2 py-2">Received</th>
              <th className="text-left text-zinc-500 text-[10px] font-medium px-2 py-2">Finished</th>
              <th className="text-right text-zinc-500 text-[10px] font-medium px-2 py-2">Orig Qty</th>
              <th className="text-right text-zinc-500 text-[10px] font-medium px-2 py-2">Remaining</th>
              <th className="text-left text-zinc-500 text-[10px] font-medium px-2 py-2">Status</th>
              <th className="text-left text-zinc-500 text-[10px] font-medium px-2 py-2">Used In</th>
              {canSeePrices && <th className="text-right text-zinc-500 text-[10px] font-medium px-2 py-2">Cost / Unit</th>}
              {canSeePrices && <th className="text-right text-zinc-500 text-[10px] font-medium px-2 py-2">Value Left</th>}
              {isAdmin && <th className="px-2 py-2 pr-4 w-16 text-right text-zinc-500 text-[10px] font-medium">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredLots.map(lot => {
              const finishedAt = lot.finished_at ? lot.finished_at.slice(0, 10) : null;
              const purchaseDate = (lot.purchase_date || '').slice(0, 10);
              const StatusIcon = lot.is_active ? Clock : CheckCircle2;
              const statusColor = lot.is_active
                ? (lot.fifo_priority ? 'text-red-400' : 'text-green-400')
                : 'text-zinc-500';
              return (
                <tr key={lot.id} className={`border-b border-zinc-800/30 hover:bg-zinc-800/20 ${!lot.is_active ? 'opacity-60' : ''}`} data-testid={`lot-row-${lot.id}`}>
                  <td className="px-2 py-1.5 pl-8">
                    <span className="font-mono text-[11px] bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1.5 py-0.5 rounded-md">
                      {lot.lot_no || '—'}
                    </span>
                    {lot.fifo_priority && <span className="ml-1 text-[9px] text-red-400" title="Use this first (FIFO priority)">⚡</span>}
                  </td>
                  <td className="px-2 py-1.5 text-zinc-400 text-[11px]">{lot.vendor_name || '—'}</td>
                  <td className="px-2 py-1.5 text-zinc-300 text-[11px] font-mono" data-testid={`lot-invoice-no-${lot.id}`}>
                    {lot.source_invoice_number
                      ? <span className="text-emerald-400" title="Linked to vendor purchase bill">{lot.source_invoice_number}</span>
                      : (lot.bill_ref
                          ? <span className="text-zinc-400" title="Manual bill ref">{lot.bill_ref}</span>
                          : <span className="text-zinc-600">—</span>)}
                  </td>
                  <td className="px-2 py-1.5 text-zinc-400 text-[11px] font-mono">{purchaseDate || '—'}</td>
                  <td className="px-2 py-1.5 text-[11px] font-mono">
                    {finishedAt
                      ? <span className="text-zinc-500">{finishedAt}</span>
                      : lot.is_active
                        ? <span className="text-green-400 font-semibold">in use</span>
                        : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-zinc-400 text-[11px]">{lot.quantity_original} {unit}</td>
                  <td className={`px-2 py-1.5 text-right font-mono text-[11px] font-semibold ${lot.is_active ? 'text-zinc-100' : 'text-zinc-600'}`}>
                    {lot.quantity_remaining} {unit}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1">
                      <StatusIcon size={11} className={statusColor} />
                      <span className={`text-[10px] ${statusColor}`}>
                        {lot.is_active ? (lot.fifo_priority ? 'Priority' : 'Active') : 'Finished'}
                      </span>
                      {lot.is_active && (
                        <span className="text-zinc-600 text-[10px]">· {lot.consumed_pct}% used</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5" data-testid={`lot-used-in-${lot.id}`}>
                    <div className="flex items-center gap-1 flex-wrap">
                      {(lot.batches || []).length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-violet-900/30 text-violet-300 border border-violet-800/40" title={(lot.batches || []).map(b => `${b.batch_number} · ${b.product_name} · ${b.qty_used} ${unit} · ${b.date}`).join('\n')}>
                          <Factory size={10} /> {lot.batches.length} batch{lot.batches.length > 1 ? 'es' : ''} · {lot.qty_in_batches} {unit}
                        </span>
                      )}
                      {(lot.sales || []).length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-900/30 text-emerald-300 border border-emerald-800/40" title={(lot.sales || []).map(s => `${s.invoice_number} · ${s.client} · ${s.qty_used} ${unit} · ${s.date}`).join('\n')}>
                          <ShoppingCart size={10} /> {lot.sales.length} sale{lot.sales.length > 1 ? 's' : ''} · {lot.qty_sold} {unit}
                        </span>
                      )}
                      {!(lot.batches || []).length && !(lot.sales || []).length && <span className="text-zinc-600 text-[10px]">—</span>}
                    </div>
                  </td>
                  {canSeePrices && (
                    <td className="px-2 py-1.5 text-right font-mono text-zinc-400 text-[11px]">
                      {lot.cost_per_unit > 0 ? `${fmt(lot.cost_per_unit)}/${unit}` : '—'}
                    </td>
                  )}
                  {canSeePrices && (
                    <td className="px-2 py-1.5 text-right font-mono text-amber-400 text-[11px]">
                      {lot.is_active ? fmt(lot.quantity_remaining * lot.cost_per_unit) : '—'}
                    </td>
                  )}
                  {isAdmin && (
                    <td className="px-2 py-1.5 text-right pr-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`trace-lot-${lot.id}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            setTraceLot({ id: lot.id, lot_no: lot.lot_no });
                            setTraceData(null); setTraceLoading(true);
                            try {
                              const r = await axios.get(`${API}/inventory/lots/${lot.id}/trace`, { withCredentials: true });
                              setTraceData(r.data);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to load trace');
                              setTraceLot(null);
                            } finally { setTraceLoading(false); }
                          }}
                          title="Trace this lot — source → batches → invoices"
                          className="p-1 rounded-md bg-violet-900/30 hover:bg-violet-900/50 text-violet-300 hover:text-violet-200 border border-violet-800/40 transition-colors">
                          <GitBranch size={11} />
                        </button>
                        <button
                          data-testid={`edit-lot-${lot.id}`}
                          onClick={(e) => { e.stopPropagation(); openEdit(lot); }}
                          title="Edit lot details"
                          className="p-1 rounded-md bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 hover:text-blue-300 border border-blue-800/30 transition-colors">
                          <Pencil size={11} />
                        </button>
                        {lot.is_active && (
                          <button
                            data-testid={`delete-lot-${lot.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDelete(
                                `/inventory/lots/${lot.id}`,
                                lot.lot_no || materialName,
                                `Delete lot ${lot.lot_no || ''}? Removes ${lot.quantity_remaining} ${unit} from stock.`,
                                () => { onChanged?.(); load(); }
                              );
                            }}
                            className="p-1 rounded-md bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 border border-red-800/30 transition-colors"
                            title="Delete this lot">
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {/* Sales history — this raw material sold directly (not via a recipe batch) */}
      {!loading && (
        <div className="mt-3 border-t border-zinc-800/60 pt-2" data-testid={`rm-sales-history-${materialId}`}>
          <button type="button" onClick={(e) => { e.stopPropagation(); setShowSales(s => !s); }}
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300"
            data-testid={`rm-sales-toggle-${materialId}`}>
            {showSales ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            <ShoppingCart size={11} className="text-emerald-400" />
            Sales History · {(data.material_sales || []).length} invoice{(data.material_sales || []).length === 1 ? '' : 's'}
            <span className="text-emerald-400 font-mono normal-case">· {data.total_sold || 0} {unit} sold direct</span>
          </button>
          {showSales && (
            (data.material_sales || []).length === 0 ? (
              <div className="text-zinc-600 text-[11px] py-2 pl-5">No direct sales of {materialName} yet — usage above is via production batches only.</div>
            ) : (
              <div className="overflow-x-auto max-w-full mt-1">
                <table className="w-full min-w-[560px]">
                  <thead>
                    <tr className="border-b border-zinc-800/60">
                      <th className="text-left text-zinc-500 text-[10px] font-medium px-2 py-1.5 pl-8">Date</th>
                      <th className="text-left text-zinc-500 text-[10px] font-medium px-2 py-1.5">Invoice</th>
                      <th className="text-left text-zinc-500 text-[10px] font-medium px-2 py-1.5">Customer</th>
                      <th className="text-left text-zinc-500 text-[10px] font-medium px-2 py-1.5">From Lot</th>
                      <th className="text-right text-zinc-500 text-[10px] font-medium px-2 py-1.5">Qty</th>
                      {canSeePrices && <th className="text-right text-zinc-500 text-[10px] font-medium px-2 py-1.5">Rate</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {data.material_sales.map((s, i) => (
                      <tr key={`${s.voucher_id}-${s.lot_no}-${i}`} className="border-b border-zinc-800/30 hover:bg-zinc-800/20" data-testid={`rm-sale-row-${s.voucher_id}`}>
                        <td className="px-2 py-1.5 pl-8 text-zinc-400 text-[11px] font-mono">{s.date || '—'}</td>
                        <td className="px-2 py-1.5 text-emerald-400 text-[11px] font-mono">{s.invoice_number || s.voucher_number || '—'}</td>
                        <td className="px-2 py-1.5 text-zinc-200 text-[11px]">{s.client || '—'}</td>
                        <td className="px-2 py-1.5"><span className="font-mono text-[10px] bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1.5 py-0.5 rounded-md">{s.lot_no || '—'}</span></td>
                        <td className="px-2 py-1.5 text-right font-mono text-zinc-100 text-[11px] font-semibold">{s.qty_used} {unit}</td>
                        {canSeePrices && <td className="px-2 py-1.5 text-right font-mono text-zinc-400 text-[11px]">{s.rate > 0 ? `${fmt(s.rate)}/${unit}` : '—'}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}

      {/* Edit Lot Modal */}
      {editLot && (
        <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" onClick={() => setEditLot(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md my-4 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="edit-lot-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-zinc-50 truncate">Edit Lot</h3>
                <p className="text-zinc-500 text-[10px] mt-0.5">{materialName} · stock auto-rebalances on qty change</p>
              </div>
              <button onClick={() => setEditLot(null)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={saveEdit} className="p-5 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">Lot No</label>
                  <input data-testid="edit-lot-no" required value={editForm.lot_no} onChange={e => setEditForm(f => ({ ...f, lot_no: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs font-mono focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Qty Remaining ({unit})</label>
                  <input data-testid="edit-lot-qty" type="number" step="any" min="0" value={editForm.quantity_remaining} onChange={e => setEditForm(f => ({ ...f, quantity_remaining: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Qty Original ({unit})</label>
                  <input type="number" step="any" min="0" value={editForm.quantity_original} onChange={e => setEditForm(f => ({ ...f, quantity_original: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Cost / {unit} (₹)</label>
                  <input type="number" step="any" min="0" value={editForm.cost_per_unit} onChange={e => setEditForm(f => ({ ...f, cost_per_unit: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Vendor</label>
                  <input value={editForm.vendor_name} onChange={e => setEditForm(f => ({ ...f, vendor_name: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Purchase Date</label>
                  <input type="date" value={editForm.purchase_date} onChange={e => setEditForm(f => ({ ...f, purchase_date: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Expiry (opt)</label>
                  <input type="date" value={editForm.expiry_date} onChange={e => setEditForm(f => ({ ...f, expiry_date: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">Bill / Invoice Ref</label>
                  <input value={editForm.bill_ref} onChange={e => setEditForm(f => ({ ...f, bill_ref: e.target.value }))} placeholder="e.g. BL-2026/0410" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="flex items-center gap-2 text-zinc-300 text-xs cursor-pointer">
                    <input type="checkbox" checked={editForm.fifo_priority} onChange={e => setEditForm(f => ({ ...f, fifo_priority: e.target.checked }))} className="accent-amber-500" />
                    <span>FIFO priority — consume this lot first (use for near-expiry stock)</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditLot(null)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button data-testid="save-lot-edit" type="submit" disabled={savingEdit} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50">{savingEdit ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* iter164 — Trace Lot modal */}
      {traceLot && (
        <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-[60] p-3 sm:p-4 overflow-y-auto"
          onClick={() => { setTraceLot(null); setTraceData(null); }}>
          <div className="bg-zinc-900 border border-violet-700/50 rounded-2xl w-full max-w-3xl my-4 max-h-[92vh] flex flex-col"
            onClick={e => e.stopPropagation()} data-testid="trace-lot-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <div>
                <h3 className="font-heading font-semibold text-zinc-50 flex items-center gap-2">
                  <GitBranch size={15} className="text-violet-400"/> Trace Lot · <span className="text-amber-400 font-mono">{traceLot.lot_no}</span>
                </h3>
                <p className="text-zinc-500 text-[10px] mt-0.5">Source bill → production batches → finished-good lots → customer invoices</p>
              </div>
              <button onClick={() => { setTraceLot(null); setTraceData(null); }} className="text-zinc-500 hover:text-zinc-200"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto p-5 space-y-4">
              {traceLoading && <div className="text-center text-zinc-500 text-sm py-8">Loading trace…</div>}
              {traceData && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    <div className="bg-zinc-800/50 rounded p-2"><div className="text-[9px] text-zinc-500 uppercase tracking-wider">Material</div><div className="text-sm text-zinc-100 font-semibold truncate">{traceData.lot.material_name}</div></div>
                    <div className="bg-zinc-800/50 rounded p-2"><div className="text-[9px] text-zinc-500 uppercase tracking-wider">Original Qty</div><div className="text-sm text-emerald-300 font-bold">{traceData.lot.quantity_original} {unit}</div></div>
                    <div className="bg-zinc-800/50 rounded p-2"><div className="text-[9px] text-zinc-500 uppercase tracking-wider">Consumed</div><div className="text-sm text-amber-300 font-bold">{traceData.lot.quantity_consumed} {unit}</div></div>
                    <div className="bg-zinc-800/50 rounded p-2"><div className="text-[9px] text-zinc-500 uppercase tracking-wider">Batches Used</div><div className="text-sm text-violet-300 font-bold">{traceData.summary.batches_count}</div></div>
                    <div className="bg-zinc-800/50 rounded p-2" data-testid="trace-sold-direct"><div className="text-[9px] text-zinc-500 uppercase tracking-wider">Sold Direct</div><div className="text-sm text-emerald-300 font-bold">{traceData.summary.total_sold || 0} {unit}</div></div>
                  </div>
                  <div className="border border-zinc-800 rounded-lg p-3" data-testid="trace-source-bill">
                    <div className="text-zinc-400 text-[10px] uppercase tracking-wider mb-1.5">① Source Purchase Bill</div>
                    {traceData.source_bill ? (
                      <div className="text-xs text-zinc-200">
                        <span className="text-amber-400 font-mono">{traceData.source_bill.invoice_number || '—'}</span>
                        <span className="text-zinc-500"> dt {(traceData.source_bill.invoice_date || '').slice(0,10) || '—'}</span>
                        <span className="text-zinc-300"> · from </span>
                        <span className="text-sky-300">{traceData.source_bill.vendor_name}</span>
                        <span className="text-zinc-500"> · ₹{(traceData.source_bill.total_amount || 0).toLocaleString()}</span>
                      </div>
                    ) : <div className="text-xs text-zinc-500">No source bill linked (legacy lot).</div>}
                  </div>
                  <div className="border border-zinc-800 rounded-lg p-3" data-testid="trace-batches">
                    <div className="text-zinc-400 text-[10px] uppercase tracking-wider mb-2">② Production Batches ({traceData.summary.batches_count})</div>
                    {traceData.consuming_batches.length === 0 ? (
                      <div className="text-xs text-zinc-500">No production batch has consumed this lot yet.</div>
                    ) : (
                      <div className="space-y-2">
                        {traceData.consuming_batches.map(b => (
                          <div key={b.batch_id} className="bg-zinc-950 border border-zinc-800 rounded p-2.5">
                            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                              <div className="text-xs">
                                <span className="text-amber-400 font-mono">{b.batch_number || b.batch_id.slice(0,8)}</span>
                                <ArrowRight size={9} className="inline mx-1 text-zinc-600"/>
                                <span className="text-zinc-200">{b.product_name}</span>
                              </div>
                              <span className="text-[10px] text-zinc-500">
                                {b.consumed_qty} {unit} consumed · {b.status} · {b.completion_date || b.start_date}
                              </span>
                            </div>
                            {b.dispatches.length > 0 && (
                              <div className="mt-1.5 ml-3 border-l-2 border-violet-500/40 pl-2">
                                <div className="text-[10px] text-zinc-500 mb-1">③ Customer dispatches ({b.dispatches.length}):</div>
                                {b.dispatches.map((d, i) => (
                                  <div key={i} className="text-[11px] text-zinc-300" data-testid={`trace-dispatch-${b.batch_id}-${i}`}>
                                    • <span className="text-emerald-400 font-mono">{d.invoice_number}</span>
                                    <span className="text-zinc-500"> ({d.invoice_date})</span>
                                    <span className="text-zinc-300"> → {d.client}</span>
                                    <span className="text-zinc-500"> · ₹{d.total_amount.toLocaleString()}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="border border-emerald-800/40 rounded-lg p-3" data-testid="trace-direct-sales">
                    <div className="text-zinc-400 text-[10px] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <ShoppingCart size={11} className="text-emerald-400" /> ④ Sold Directly as Raw Material ({(traceData.direct_sales || []).length})
                    </div>
                    {(traceData.direct_sales || []).length === 0 ? (
                      <div className="text-xs text-zinc-500">This lot has not been sold directly — only used in production above.</div>
                    ) : (
                      <div className="space-y-1">
                        {traceData.direct_sales.map((s, i) => (
                          <div key={`${s.voucher_id}-${i}`} className="flex items-center justify-between flex-wrap gap-2 bg-zinc-950 border border-zinc-800 rounded p-2 text-[11px]" data-testid={`trace-sale-${s.voucher_id}`}>
                            <div>
                              <span className="text-emerald-400 font-mono">{s.invoice_number || s.voucher_number}</span>
                              <span className="text-zinc-500"> ({s.date})</span>
                              <span className="text-zinc-300"> → {s.client}</span>
                            </div>
                            <span className="text-zinc-200 font-mono">{s.qty_used} {unit}{s.rate > 0 ? <span className="text-zinc-500"> @ ₹{s.rate}</span> : null}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {traceData.summary.downstream_customers.length > 0 && (
                    <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3">
                      <div className="text-violet-300 text-[10px] uppercase tracking-wider font-bold mb-1">Recall Footprint</div>
                      <div className="text-xs text-zinc-200">
                        {traceData.summary.downstream_invoice_count} invoice(s) to {traceData.summary.downstream_customers.length} customer(s): <span className="text-violet-200">{traceData.summary.downstream_customers.join(', ')}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
